import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, QrCode, Globe, Copy, ExternalLink, Save, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useStandort } from "../hooks/useStandort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Menue = () => {
  const { activeStandortId } = useStandort();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {item, typ}

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const res = await axios.get(`${API}/menue`, { params, withCredentials: true });
      setData(res.data);
      if (res.data.public_token) {
        setToken(res.data.public_token);
        setPublicUrl(`${window.location.origin}/m/${res.data.public_token}`);
      } else {
        setToken(null);
        setPublicUrl("");
      }
    } catch (e) {
      toast.error("Fehler beim Laden des Menüs");
    } finally {
      setLoading(false);
    }
  }, [activeStandortId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePublish = async () => {
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const res = await axios.post(`${API}/menue/publish`, {}, { params, withCredentials: true });
      setToken(res.data.token);
      setPublicUrl(`${window.location.origin}/m/${res.data.token}`);
      toast.success("Speisekarte veröffentlicht");
    } catch (e) {
      toast.error("Fehler beim Veröffentlichen");
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm("Veröffentlichung wirklich aufheben? QR-Code wird ungültig.")) return;
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      await axios.delete(`${API}/menue/publish`, { params, withCredentials: true });
      setToken(null);
      setPublicUrl("");
      toast.success("Speisekarte unveröffentlicht");
    } catch (e) {
      toast.error("Fehler beim Aufheben");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const res = await axios.get(`${API}/menue/pdf`, { params, withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Speisekarte.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF-Download fehlgeschlagen");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link kopiert");
  };

  const startEdit = (item, typ) => {
    setEditing({
      ...item,
      typ,
      allergene: item.allergene || [],
      zusatzstoffe: item.zusatzstoffe || [],
      auf_speisekarte: item.auf_speisekarte !== false,
    });
    setEditorOpen(true);
  };

  const toggleAllergen = (code) => {
    setEditing((prev) => ({
      ...prev,
      allergene: prev.allergene.includes(code)
        ? prev.allergene.filter((c) => c !== code)
        : [...prev.allergene, code].sort(),
    }));
  };

  const toggleZusatzstoff = (code) => {
    setEditing((prev) => ({
      ...prev,
      zusatzstoffe: prev.zusatzstoffe.includes(code)
        ? prev.zusatzstoffe.filter((c) => c !== code)
        : [...prev.zusatzstoffe, code].sort((a, b) => parseInt(a) - parseInt(b)),
    }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    const collection = editing.typ === "speise" ? "speisen" : "getraenke";
    try {
      await axios.put(
        `${API}/${collection}/${editing.id}`,
        {
          allergene: editing.allergene,
          zusatzstoffe: editing.zusatzstoffe,
          auf_speisekarte: editing.auf_speisekarte,
          beschreibung: editing.beschreibung,
        },
        { withCredentials: true }
      );
      toast.success("Aktualisiert");
      setEditorOpen(false);
      setEditing(null);
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Laden...</div>;
  if (!data) return null;

  const renderGruppen = (gruppen, typ) => {
    if (!gruppen || gruppen.length === 0) {
      return (
        <p className="text-sm text-slate-500 italic py-4">
          Noch keine {typ === "speise" ? "Speisen" : "Getränke"} auf der Karte.
        </p>
      );
    }
    return gruppen.map((g) => (
      <div key={g.kategorie} className="mb-6">
        <h3 className="font-display text-sm font-semibold text-[#3A4D39] dark:text-violet-300 uppercase tracking-wide mb-2">{g.kategorie}</h3>
        <div className="space-y-2">
          {g.items.map((it) => (
            <div
              key={it.id}
              className="flex items-start justify-between gap-3 p-3 bg-stone-50 dark:bg-slate-900/40 border border-stone-200 dark:border-slate-700 rounded-sm hover:border-[#3A4D39] dark:hover:border-violet-500 cursor-pointer transition-colors"
              onClick={() => startEdit(it, typ)}
              data-testid={`menue-item-${it.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{it.name}</span>
                  {it.allergene?.length > 0 && (
                    <span className="text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 px-1.5 py-0.5 rounded">
                      {it.allergene.join(",")}
                    </span>
                  )}
                  {it.zusatzstoffe?.length > 0 && (
                    <span className="text-[10px] font-bold bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded">
                      [{it.zusatzstoffe.join(",")}]
                    </span>
                  )}
                </div>
                {it.beschreibung && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{it.beschreibung}</p>
                )}
              </div>
              <span className="font-mono font-semibold text-sm text-[#3A4D39] dark:text-violet-300 whitespace-nowrap">
                {it.verkaufspreis.toFixed(2)} €
              </span>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div data-testid="menue-page">
      <header className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="page-title">Menü-Generator</h1>
            <p className="page-subtitle">Allergene & Zusatzstoffe verwalten · PDF · QR-Code · Digitale Speisekarte</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleDownloadPDF} variant="outline" className="rounded-sm" data-testid="btn-menu-pdf">
              <FileText size={14} className="mr-1.5" /> PDF
            </Button>
            {token ? (
              <>
                <Button onClick={() => setQrOpen(true)} variant="outline" className="rounded-sm" data-testid="btn-menu-qr">
                  <QrCode size={14} className="mr-1.5" /> QR-Code
                </Button>
                <Button onClick={handleUnpublish} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" data-testid="btn-menu-unpublish">
                  Unveröffentlichen
                </Button>
              </>
            ) : (
              <Button onClick={handlePublish} className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm" data-testid="btn-menu-publish">
                <Globe size={14} className="mr-1.5" /> Veröffentlichen
              </Button>
            )}
          </div>
        </div>
      </header>

      {token && publicUrl && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-sm p-3 mb-6 flex items-center gap-2 flex-wrap" data-testid="menue-public-link">
          <Globe size={14} className="text-emerald-700 dark:text-emerald-300" />
          <span className="text-xs text-emerald-700 dark:text-emerald-300 font-mono break-all flex-1">{publicUrl}</span>
          <Button size="sm" variant="ghost" onClick={copyLink} className="text-xs"><Copy size={12} className="mr-1" /> Kopieren</Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button size="sm" variant="ghost" className="text-xs"><ExternalLink size={12} className="mr-1" /> Öffnen</Button>
          </a>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-5 rounded-sm">
          <h2 className="font-display text-base font-bold text-[#3A4D39] dark:text-violet-300 mb-4">Speisen</h2>
          {renderGruppen(data.speisen, "speise")}
        </div>
        <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-5 rounded-sm">
          <h2 className="font-display text-base font-bold text-[#3A4D39] dark:text-violet-300 mb-4">Getränke</h2>
          {renderGruppen(data.getraenke, "getraenk")}
        </div>
      </div>

      {/* Editor-Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 justify-between">
                <Label className="text-xs flex items-center gap-2">
                  {editing.auf_speisekarte ? <Eye size={14} /> : <EyeOff size={14} />}
                  Auf Speisekarte sichtbar
                </Label>
                <Switch
                  checked={editing.auf_speisekarte}
                  onCheckedChange={(c) => setEditing({ ...editing, auf_speisekarte: c })}
                  data-testid="toggle-auf-speisekarte"
                />
              </div>
              <div>
                <Label className="text-xs">Beschreibung</Label>
                <Input
                  value={editing.beschreibung || ""}
                  onChange={(e) => setEditing({ ...editing, beschreibung: e.target.value })}
                  placeholder="z.B. Klassischer Kabeljau im Bierteig…"
                  data-testid="input-beschreibung"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">Allergene</Label>
                <div className="flex flex-wrap gap-1">
                  {data.allergene_liste.map((a) => {
                    const active = editing.allergene.includes(a.code);
                    return (
                      <button
                        key={a.code}
                        type="button"
                        onClick={() => toggleAllergen(a.code)}
                        title={a.name}
                        data-testid={`allergen-${a.code}`}
                        className={`text-xs px-2 py-1 rounded border ${active ? "bg-orange-500 text-white border-orange-500" : "bg-stone-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-stone-200 dark:border-slate-600 hover:border-orange-400"}`}
                      >
                        <span className="font-bold">{a.code}</span> <span className="opacity-80">{a.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">Zusatzstoffe</Label>
                <div className="flex flex-wrap gap-1">
                  {data.zusatzstoffe_liste.map((z) => {
                    const active = editing.zusatzstoffe.includes(z.code);
                    return (
                      <button
                        key={z.code}
                        type="button"
                        onClick={() => toggleZusatzstoff(z.code)}
                        title={z.name}
                        data-testid={`zusatzstoff-${z.code}`}
                        className={`text-xs px-2 py-1 rounded border ${active ? "bg-slate-700 text-white border-slate-700" : "bg-stone-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-stone-200 dark:border-slate-600 hover:border-slate-500"}`}
                      >
                        <span className="font-bold">{z.code}</span> <span className="opacity-80">{z.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Abbrechen</Button>
            <Button onClick={saveEdit} className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white" data-testid="btn-save-allergens"><Save size={14} className="mr-1.5" /> Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>QR-Code zur Speisekarte</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4 space-y-3">
            {token && (
              <img
                src={`${API}/menue/${token}/qr`}
                alt="QR-Code"
                className="w-64 h-64 border border-stone-200 dark:border-slate-700 p-2 bg-white"
                data-testid="qr-code-image"
              />
            )}
            <p className="text-xs text-slate-500 text-center">Drucken, am Tisch auflegen, Gäste scannen mit dem Smartphone.</p>
            <a
              href={`${API}/menue/${token}/qr`}
              download="Speisekarte_QR.png"
              className="text-xs underline text-[#3A4D39] dark:text-violet-300"
            >
              QR-Code als PNG herunterladen
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Menue;
